/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers

  return distance;
};

/**
 * Convert degrees to radians
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<{latitude: number, longitude: number}>} User's coordinates
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Filter cars within specified radius from user location
 * @param {Array} cars - Array of car objects
 * @param {Object} userLocation - User's location {latitude, longitude}
 * @param {number} radiusKm - Radius in kilometers (default: 10)
 * @returns {Array} Filtered cars with distance property
 */
/**
 * Get city name from coordinates using reverse geocoding (OpenStreetMap Nominatim)
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<{city: string, state: string, country: string}>} Location details
 */
export const getCityFromCoordinates = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'CarBookingApp/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch location');
    }

    const data = await response.json();
    const address = data.address || {};

    return {
      city: address.city || address.town || address.village || address.municipality || address.county || '',
      state: address.state || '',
      country: address.country || '',
      displayName: data.display_name || ''
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return { city: '', state: '', country: '', displayName: '' };
  }
};

/**
 * Get user location with city name
 * @returns {Promise<{latitude: number, longitude: number, city: string, state: string}>}
 */
export const getUserLocationWithCity = async () => {
  const coords = await getUserLocation();
  const locationDetails = await getCityFromCoordinates(coords.latitude, coords.longitude);

  return {
    ...coords,
    ...locationDetails
  };
};

export const filterCarsByDistance = (cars, userLocation, radiusKm = 10) => {
  if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
    return cars;
  }

  return cars
    .map(car => {
      // Check if car has location coordinates
      if (car.location?.coordinates?.latitude && car.location?.coordinates?.longitude) {
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          car.location.coordinates.latitude,
          car.location.coordinates.longitude
        );
        return { ...car, distance };
      }
      return { ...car, distance: null };
    })
    .filter(car => car.distance === null || car.distance <= radiusKm)
    .sort((a, b) => {
      // Sort by distance, cars without distance at the end
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
};
